"use client";

import { useEffect, useRef } from 'react';

const GREEK = ['Θ', 'Δ', 'Γ', 'Ω', 'Σ', 'Φ', 'Ψ', 'Λ', 'α', 'β', 'γ', 'δ', 'σ', 'ρ', 'μ', 'π', 'ε', 'ζ', 'η', 'ν', 'ξ'];
const COLORS = ['#F97316', '#3B82F6', '#8B5CF6', '#06B6D4', '#F59E0B', '#EC4899'];

interface Particle {
    x: number; y: number;
    ox: number; oy: number;
    vx: number; vy: number;
    letter: string;
    size: number;
    color: string;
    opacity: number;
    rotation: number;
    rotSpeed: number;
}

// Full-page ambient background: a dark radial vignette, three slow pulsing
// color orbs, and a canvas of floating Greek letters that repel from the
// cursor. The single shared implementation behind auth screens
// (login/register/verify-email) AND every market-closed / operational-hours
// gate screen (MarketClosedAnimation, OperationalGate's card, dashboard/
// [slug]'s embedded-app gate) — they used to each carry their own copy of
// this canvas/particle code. Deliberately does NOT check
// prefers-reduced-motion: always animates, everywhere, by design.
export default function GreekParticleBackground({ count = 55 }: { count?: number }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouseRef = useRef({ x: -9999, y: -9999 });
    const particlesRef = useRef<Particle[]>([]);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            initParticles();
        };

        const initParticles = () => {
            const w = canvas.width, h = canvas.height;
            particlesRef.current = Array.from({ length: count }, () => {
                const x = Math.random() * w;
                const y = Math.random() * h;
                return {
                    x, y, ox: x, oy: y,
                    vx: (Math.random() - 0.5) * 0.4,
                    vy: (Math.random() - 0.5) * 0.4,
                    letter: GREEK[Math.floor(Math.random() * GREEK.length)],
                    size: 14 + Math.random() * 46,
                    color: COLORS[Math.floor(Math.random() * COLORS.length)],
                    opacity: 0.08 + Math.random() * 0.2,
                    rotation: Math.random() * Math.PI * 2,
                    rotSpeed: (Math.random() - 0.5) * 0.008,
                };
            });
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const mx = mouseRef.current.x;
            const my = mouseRef.current.y;

            for (const p of particlesRef.current) {
                // Mouse repulsion
                const dx = p.x - mx, dy = p.y - my;
                const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                const repulseRadius = 160;
                if (dist < repulseRadius) {
                    const force = (repulseRadius - dist) / repulseRadius;
                    p.vx += (dx / dist) * force * 3.2;
                    p.vy += (dy / dist) * force * 3.2;
                }

                // Spring back to origin
                p.vx += (p.ox - p.x) * 0.018;
                p.vy += (p.oy - p.y) * 0.018;

                // Damping
                p.vx *= 0.88;
                p.vy *= 0.88;

                p.x += p.vx;
                p.y += p.vy;
                p.rotation += p.rotSpeed;

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);
                ctx.globalAlpha = p.opacity;
                ctx.fillStyle = p.color;
                ctx.font = `${p.size}px 'Georgia', serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowColor = p.color;
                ctx.shadowBlur = 18;
                ctx.fillText(p.letter, 0, 0);
                ctx.restore();
            }

            rafRef.current = requestAnimationFrame(animate);
        };

        resize();
        animate();

        const onMouse = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        };
        const onLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', onMouse);
        canvas.addEventListener('mouseleave', onLeave);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', onMouse);
            canvas.removeEventListener('mouseleave', onLeave);
        };
    }, [count]);

    return (
        <div
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden"
            style={{ background: 'radial-gradient(ellipse at 60% 40%, #0f172a 0%, #030712 100%)' }}
        >
            {/* Ambient orbs */}
            <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
                style={{ background: 'radial-gradient(circle, #F97316, transparent)' }} />
            <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full opacity-[0.08] blur-3xl animate-pulse"
                style={{ background: 'radial-gradient(circle, #3B82F6, transparent)', animationDelay: '1.5s' }} />
            <div className="absolute top-2/3 left-1/2 w-64 h-64 rounded-full opacity-[0.06] blur-3xl animate-pulse"
                style={{ background: 'radial-gradient(circle, #8B5CF6, transparent)', animationDelay: '3s' }} />

            <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
        </div>
    );
}
